import React, { useEffect, useState, useRef } from 'react';
import { collection, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { firestore } from './../../FirebaseUtils';
import QuestionCard from './QuestionCard';
import { Alert, Button, Table } from 'react-bootstrap';

function AdminQuestionSetup() {
    const [ loading, setLoading ] = useState(true);
    const [ errorMessage, setErrorMessage ] = useState();

    const [ processing, setProcessing ] = useState(false);

    const [ editMode, setEditMode ] = useState(false);
    const [ editingDocId, setEditingDocId ] = useState();

    const [questions, setQuestions] = useState([]);

    const defaultChoices = [ "", "", "", "" ];
    const [ choices, setChoices ] = useState(defaultChoices);
    const [ choiceLabels, setChoiceLabels ] = useState(["ก", "ข", "ค", "ง"]);
    const [ answer, setAnswer ] = useState(-1);

    const questionRef = useRef();
    const choicesRef = useRef([]);
    const choicesRadioRef = useRef([]);

    useEffect(() => {
        // Get a reference to the "questions" collection
        const questionsRef = collection(firestore, 'questions');

        // Listen for changes in the collection
        const unsubscribe = onSnapshot(questionsRef, (snapshot) => {
            setErrorMessage("");
            setLoading(true);
    
            const questionData = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data()
            }));
            setQuestions(questionData);

            setLoading(false);
        });

        // Clean up the listener on unmount
        return () => unsubscribe();

    }, []);

    async function onClickAddQuestionButton(e) {

        const now = new Date();
        const docId = now.getFullYear() + ('0' + (now.getMonth() + 1).toString()).slice(-2) + ('0' + now.getDate()).slice(-2) + ('0' + now.getHours()).slice(-2) + ('0' + now.getMinutes()).slice(-2) + ('0' + now.getSeconds()).slice(-2);

        await setupQuestion(docId, questions.length);
    }

    function onClickEditButton(docId) {
        setErrorMessage(null);

        setEditMode(true);

        setEditingDocId(docId);

        let q = questions.find((element) => element.id === docId);

        questionRef.current.value = q.question;

        choicesRef.current[0].value = q.choices[0];
        choicesRef.current[1].value = q.choices[1];
        choicesRef.current[2].value = q.choices[2];
        choicesRef.current[3].value = q.choices[3];

        choicesRadioRef.current[0].checked = (q.answer === 0);
        choicesRadioRef.current[1].checked = (q.answer === 1);
        choicesRadioRef.current[2].checked = (q.answer === 2);
        choicesRadioRef.current[3].checked = (q.answer === 3);

        setChoices([...q.choices]);
        setAnswer(q.answer);
    }

    async function onClickSaveQuestionButton(e) {
        let q = questions.find((element) => element.id === editingDocId);

        await setupQuestion(editingDocId, q.order);

        setEditMode(false);
    }

    function onClickCancelEditQuestionButton(e) {
        setEditMode(false);
        setEditingDocId(null);

        clearQuestionForm();
    }

    async function setupQuestion(docId, order) {
        setProcessing(true);

        if (answer < 0 || answer > choices.length) {
            setErrorMessage('กรุณาเลือกคำตอบที่ถูกต้อง');
            setProcessing(false);
            return;
        }

        if (questionRef.current.value.trim() == "") {
            setErrorMessage('กรุณากรอกคำถาม');
            setProcessing(false);
            return;
        }

        for (let i = 0; i < choices.length; i++) {
            if (choices[i].trim() == "") {
                setErrorMessage(`กรุณากรอกตัวเลือกคำตอบให้ครบถ้วน\nกรุณากรอกข้อ ${choiceLabels[i]}`);
                setProcessing(false);
                return;
            }
        }

        setErrorMessage(null);

        const docContent = {
            order: order,
            question: questionRef.current.value.trim(),
            choices: choices,
            answer: answer,
        };

        try {
            await setDoc(doc(firestore, "questions", docId), docContent);

            clearQuestionForm();

            // Optionally, you can display a success message or update the UI
            console.log('Question added successfully!');
        } 
        catch (error) {
            console.error('Error adding question:', error);
            // Handle the error appropriately (e.g., display an error message)
        }

        setProcessing(false);
    }

    function clearQuestionForm() {
        questionRef.current.value = "";

        for (let i = 0; i < choicesRef.current.length; i++) {
            choicesRef.current[i].value = "";
        }
        for (let i = 0; i < choicesRadioRef.current.length; i++) {
            choicesRadioRef.current[i].checked = false;
        }

        setChoices([...defaultChoices]);
        setAnswer(-1);
    }

    async function onConfirmDeleteButton(docId) {
        console.log('deleting ', docId);

        setProcessing(true);

        let q = questions.find((element) => element.id === docId);
        let deletedOrder = q.order;

        await deleteDoc(doc(firestore, "questions", docId));

        for (let i = 0; i < questions.length; i++) {

            console.log('checking ', JSON.stringify(questions[i]));

            if (questions[i].order > deletedOrder) {

                let docContent = {...questions[i]};
                delete docContent.id;
                docContent.order = docContent.order - 1;

                try {
                    await setDoc(doc(firestore, "questions", questions[i].id), docContent);
                } 
                catch (error) {
                    console.error('Error editing order in question:', error);
                }
            }
        }

        setProcessing(false);
    }

    async function onClickUpButton(docId) {
        let q1 = questions.find((element) => element.id === docId);

        if (q1.order === 0) return;

        let q2 = questions.find((element) => element.order === q1.order - 1);

        if (!q2) return;

        setProcessing(true);

        try {
            let docContent = {...q1};
            delete docContent.id;
            docContent.order = docContent.order - 1;
            await setDoc(doc(firestore, "questions", q1.id), docContent);
        } 
        catch (error) {
            console.error(`Error editing order in question: ${q1.question}`, error);
        }

        try {
            let docContent = {...q2};
            delete docContent.id;
            docContent.order = docContent.order + 1;
            await setDoc(doc(firestore, "questions", q2.id), docContent);
        } 
        catch (error) {
            console.error(`Error editing order in question: ${q2.question}`, error);
        }

        setProcessing(false);
    }

    async function onClickDownButton(docId) {
        let q1 = questions.find((element) => element.id === docId);

        if (q1.order + 1 >= questions.length) return;

        let q2 = questions.find((element) => element.order === q1.order + 1);

        if (!q2) return;

        try {
            let docContent = {...q1};
            delete docContent.id;
            docContent.order = docContent.order + 1;
            await setDoc(doc(firestore, "questions", q1.id), docContent);
        } 
        catch (error) {
            console.error(`Error editing order in question: ${q1.question}`, error);
        }

        try {
            let docContent = {...q2};
            delete docContent.id;
            docContent.order = docContent.order - 1;
            await setDoc(doc(firestore, "questions", q2.id), docContent);
        } 
        catch (error) {
            console.error(`Error editing order in question: ${q2.question}`, error);
        }

    }

    function onAnswerChanged(e) {
        setAnswer(Number(e.target.value));

        console.log('answer = ', e.target.value)
    }

    function onChoiceChange(e) {
        let id = Number(e.target.id);
        
        let newChoices = [...choices];
        newChoices[id] = e.target.value.trim();
        setChoices(newChoices);
    }

    const inputWidth = '800px';

    return (
        <div>
            Question Setup
            <div className='mt-3' style={{ border: '1px solid #aaa', padding: '10px 10px 10px 10px' }}>
                <Table>
                    <tbody>
                        <tr>
                            <td>คำถาม</td>
                            <td>
                                <textarea type="text" style={{ width: inputWidth, height: '100px' }} ref={questionRef} readOnly={processing} />
                            </td>
                        </tr>

                        {
                            choices.map((choice, i) => {
                                return (
                                    <tr key={`choice_row_${i}`}>
                                        <td>
                                            <input 
                                                type="radio" 
                                                id={`choice${i}`} 
                                                name="choices" 
                                                value={i} 
                                                defaultChecked={i === answer} 
                                                onChange={onAnswerChanged} 
                                                ref={el => choicesRadioRef.current[i] = el}
                                            />
                                            &nbsp;
                                            <label >{choiceLabels[i]}</label>
                                        </td>
                                        <td>
                                            <input 
                                                type="text" 
                                                id={i} 
                                                style={{ width: inputWidth }} 
                                                onChange={onChoiceChange} 
                                                ref={el => choicesRef.current[i] = el} 
                                                readOnly={processing}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        }
                    </tbody>
                </Table>
                { errorMessage ? <Alert variant='danger'>{errorMessage}</Alert> : null }
                <div style={{ display: 'flex', flex: '1', direction: 'row', justifyContent: 'center' }}>
                { 
                    editMode === false ? 
                        <Button onClick={onClickAddQuestionButton} disabled={processing}>+++ เพิ่มคำถาม +++</Button> 
                    :
                        <div>
                            <Button onClick={onClickSaveQuestionButton} disabled={processing}>บันทึกการเปลี่ยนแปลง</Button>
                            &nbsp;
                            <Button variant='outline-primary' onClick={onClickCancelEditQuestionButton} disabled={processing}>ยกเลิกการเปลี่ยนแปลง</Button>
                        </div> 
                }
                </div>
            </div>
            <div className='mt-5'>
            {
                loading ?
                    <div>Please wait while retrieving data from the database...</div>
                :
                    questions.sort((a, b) => a.order - b.order).map((q, i) => {
                        return (
                            <QuestionCard 
                                key={`card${i}`} 
                                isEditing={q.id === editingDocId}
                                isProcessing={processing}
                                docId={q.id}
                                order={q.order}
                                question={q.question} 
                                choices={q.choices} 
                                answer={q.answer} 
                                onClickEditButton={onClickEditButton}
                                onConfirmDeleteButton={onConfirmDeleteButton}
                                onClickUpButton={onClickUpButton}
                                onClickDownButton={onClickDownButton}
                            />
                        );
                    })
            }
            </div>
            <div className='mb-5'></div>
        </div>
    );
}

export default AdminQuestionSetup;
